export type PointKind = 'poll' | 'event' | 'command';

export type SnapshotPoint = {
  key: string;
  kind: PointKind;
};

export type SnapshotDevice = {
  id: string;
  type: string;
  connection?: string;
  points: SnapshotPoint[];
};

export type SnapshotConnection = {
  id: string;
  type: string;
  label: string;
  points: SnapshotPoint[];
};

export type ControllerSnapshot = {
  connections: SnapshotConnection[];
  devices: SnapshotDevice[];
  /** Controller link is Zigbee (WiFi/MQTT stays off). */
  zigbee?: boolean;
};

const POLL_POINTS: Record<string, SnapshotPoint[]> = {
  sht21: [
    { key: 'temp', kind: 'poll' },
    { key: 'rh', kind: 'poll' },
  ],
  vl53l0x: [
    { key: 'mm', kind: 'poll' },
    { key: 'valid', kind: 'poll' },
  ],
};

const EVENT_POINTS: Record<string, SnapshotPoint[]> = {
  sml: [
    { key: 'energy', kind: 'event' },
    { key: 'power', kind: 'event' },
    { key: 'energy_reverse', kind: 'event' },
  ],
  gps: [
    { key: 'lat', kind: 'event' },
    { key: 'lon', kind: 'event' },
    { key: 'valid', kind: 'event' },
  ],
  victron: [
    { key: 'v', kind: 'event' },
    { key: 'i', kind: 'event' },
    { key: 'vpv', kind: 'event' },
    { key: 'ppv', kind: 'event' },
    { key: 'soc', kind: 'event' },
  ],
  pn532: [
    { key: 'uid', kind: 'event' },
    { key: 'present', kind: 'event' },
  ],
};

function asObject(raw: unknown): Record<string, unknown> {
  return raw && typeof raw === 'object' && !Array.isArray(raw) ? raw as Record<string, unknown> : {};
}

function asList(raw: unknown): Record<string, unknown>[] {
  return Array.isArray(raw)
    ? raw.filter((item) => item && typeof item === 'object' && !Array.isArray(item)) as Record<string, unknown>[]
    : [];
}

function pointsForType(type: string, device: Record<string, unknown>): SnapshotPoint[] {
  if (POLL_POINTS[type]) {
    return POLL_POINTS[type];
  }
  if (EVENT_POINTS[type]) {
    return EVENT_POINTS[type];
  }
  if (type === 'modbus_slave') {
    return asList(device.points).map((point) => ({
      key: String(point.key || '').trim(),
      kind: 'poll' as const,
    })).filter((point) => point.key);
  }
  if (type === 'gpio_input') {
    return asList(device.lines).map((line) => ({
      key: String(line.key || '').trim(),
      // Poll with a Flow timer (stacklight-legacy); interrupt edge still in line.edge.
      kind: 'poll' as const,
    })).filter((point) => point.key);
  }
  if (type === 'gpio_led' || type === 'gpio_buzzer') {
    return [{ key: 'on', kind: 'command' }];
  }
  if (type === 'gpio_relay') {
    const pins = Array.isArray(device.pins)
      ? device.pins.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n >= 0)
      : [];
    const count = Math.min(8, pins.length || Number(device.count) || 0);
    return Array.from({ length: count }, (_, index) => ({
      key: String(index + 1),
      kind: 'command' as const,
    }));
  }
  if (type === 'stacklight') {
    return [{ key: 'status', kind: 'command' }];
  }
  return [];
}

function pushConnection(list: SnapshotConnection[], row: Omit<SnapshotConnection, 'points'> & { points?: SnapshotPoint[] }) {
  if (!row.id) {
    return;
  }
  const existing = list.find((item) => item.id === row.id);
  const extra = row.points || [];
  if (existing) {
    existing.points.push(...extra);
    return;
  }
  list.push({ id: row.id, type: row.type, label: row.label, points: [...extra] });
}

/** Hardware tree for the Flow palette. Pins stay in settings; the editor only reads ids. */
export function snapshotFromSettings(raw: unknown): ControllerSnapshot {
  const settings = asObject(raw);
  const connections: SnapshotConnection[] = [];
  const devices: SnapshotDevice[] = [];

  for (const row of asList(settings.connections)) {
    const id = String(row.id || '').trim();
    const type = String(row.type || '').trim();
    if (!id || !type) {
      continue;
    }
    const points = asList(row.registers).map((reg) => ({
      key: String(reg.key || '').trim(),
      kind: 'command' as const,
    })).filter((point) => point.key);
    pushConnection(connections, { id, type, label: String(row.label || id), points });
  }

  const mqtt = asObject(settings.mqtt);
  if (mqtt.enable === true || mqtt.server) {
    pushConnection(connections, { id: 'mqtt', type: 'mqtt', label: 'mqtt' });
  }
  const i2c = asObject(settings.i2c);
  const sensor = asObject(settings.sensor);
  if (i2c.enable === true || i2c.sda_pin != null || sensor.sda_pin != null) {
    pushConnection(connections, { id: 'i2c', type: 'i2c', label: 'i2c' });
  }
  const modbus = asObject(settings.modbus);
  if (modbus.enable === true || modbus.baud != null) {
    pushConnection(connections, { id: 'rs485', type: 'modbus', label: 'rs485' });
  }
  const serial = asObject(settings.serial);
  if (serial.enable === true || serial.mode) {
    pushConnection(connections, { id: 'uart', type: 'uart', label: 'uart' });
  }

  for (const row of asList(settings.devices)) {
    const id = String(row.id || '').trim();
    const type = String(row.type || '').trim();
    if (!id || !type) {
      continue;
    }
    if (type === 'mqtt' || type === 'modbus' || type === 'i2c' || type === 'uart' || type === 'serial') {
      pushConnection(connections, {
        id,
        type: type === 'serial' ? 'uart' : type,
        label: id,
      });
      continue;
    }
    const connection = String(row.connection || '').trim() || undefined;
    if (type === 'sht21' && !connections.some((item) => item.type === 'i2c')) {
      pushConnection(connections, { id: connection || 'i2c', type: 'i2c', label: connection || 'i2c' });
    }
    if (type === 'sml' && !connections.some((item) => item.type === 'uart')) {
      pushConnection(connections, { id: connection || 'uart', type: 'uart', label: connection || 'uart' });
    }
    if (type === 'modbus_slave' && !connections.some((item) => item.type === 'modbus')) {
      pushConnection(connections, { id: connection || 'rs485', type: 'modbus', label: connection || 'rs485' });
    }
    devices.push({
      id,
      type,
      connection,
      points: pointsForType(type, row),
    });
  }

  return {
    connections,
    devices,
    zigbee: asObject(settings.zigbee).enable === true,
  };
}
